import React, { useCallback, useMemo } from 'react';
import isNil from 'lodash/isNil';
import moment from 'moment';
import ReactDatePicker from 'react-datepicker';
import { ContextualMenuItemType, IContextualMenuItem } from '@fluentui/react';
import {
  IComparisonConfig,
  QueryType,
  TimeUnit,
} from '../../../../../../utils/apollo/graphqlSchema';
import KubitContextualMenu from '../../../../../../components/Dropdown/ContextualMenu';
import {
  getComparisonEndDate,
  getComparisonRangeDisplay,
} from '../../../../../../utils/comparisonConfig';
import * as ComparisonConfigPickerUI from './ComparisonConfigPickerUI';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/hooks';
import { setComparisonConfig } from '../../../../../../redux/slices/oldBuilder';
import DateRangeCustomHeader from '../../../../../../components/Builder/DatePickerWidget/DateRangePicker/DateRangeCustomHeader';
import * as BuilderUI from '../../../../../../components/Builder/BuilderUI';
import { queryComparisonConfigInfo } from '../../../../../../components/KubitTooltip/globalTooltips';
import {
  builderSelectedOptionsSelector,
  comparisonConfigSelector,
} from '../../../../../../redux/selectors/builder';
import { assertCondition } from '../../../../../../utils/types';
import { builderDateSelector } from '../../../../../../redux/selectors/date';
import ContextualMenuOption from '../../../../../../components/Dropdown/ContextualMenu/ContextualMenuOption';
import BorderedDropdownLabel from '../../../../../../components/Dropdown/BorderedDropdownLabel';
import KubitTooltip from '../../../../../../components/KubitTooltip';
import { SquareRemoveButton } from '../../../../../../components/Button';

// Our pre-defined (static) comparison options (keep the names unique)
const staticDropdownOptions: {
  name: string;
  comparisonConfig: IComparisonConfig;
}[] = [
  {
    name: 'Previous Day',
    comparisonConfig: {
      __typename: 'ComparisonConfig',
      offset: 1,
      timeUnit: TimeUnit.Day,
    },
  },
  {
    name: 'Previous Week',
    comparisonConfig: {
      __typename: 'ComparisonConfig',
      offset: 1,
      timeUnit: TimeUnit.Week,
    },
  },
  {
    name: 'Previous Month',
    comparisonConfig: {
      __typename: 'ComparisonConfig',
      offset: 1,
      timeUnit: TimeUnit.Month,
    },
  },
  {
    name: 'Previous Quarter',
    comparisonConfig: {
      __typename: 'ComparisonConfig',
      offset: 1,
      timeUnit: TimeUnit.Quarter,
    },
  },
  {
    name: 'Previous Year',
    comparisonConfig: {
      __typename: 'ComparisonConfig',
      offset: 1,
      timeUnit: TimeUnit.Year,
    },
  },
];

export default () => {
  const dispatch = useAppDispatch();

  const comparisonConfig = useAppSelector(comparisonConfigSelector);
  const selectedOptions = useAppSelector((state) =>
    builderSelectedOptionsSelector(state),
  );
  assertCondition(
    selectedOptions?.type === QueryType.Expression ||
      selectedOptions?.type === QueryType.DataTable,
  );

  const builderDates = useAppSelector(builderDateSelector);
  const queryEnd = builderDates.endDate;

  const handleSetComparisonConfig = useCallback(
    (comparisonConfig: IComparisonConfig | null) => {
      dispatch(setComparisonConfig({ comparisonConfig }));
    },
    [dispatch],
  );

  const selectedCalendarDate =
    getComparisonEndDate(comparisonConfig, queryEnd) ??
    moment(queryEnd).toDate();

  // the dropdown menu options, memoized
  const dropdownItems: IContextualMenuItem[] = useMemo(() => {
    // The static, pre-defined options
    const result: IContextualMenuItem[] = staticDropdownOptions.map(
      (staticOpt) => ({
        key: staticOpt.name,
        onRenderContent: () => (
          <ContextualMenuOption
            label={staticOpt.name}
            onClick={() => {
              handleSetComparisonConfig(staticOpt.comparisonConfig);
            }}
          />
        ),
      }),
    );

    // The calendar opening item
    const onDatePickerChange = (selectedDate: Date | null) => {
      if (selectedDate == null) {
        handleSetComparisonConfig(null);
      } else {
        const offset = moment(queryEnd).diff(selectedDate, 'days');
        handleSetComparisonConfig({
          __typename: 'ComparisonConfig',
          offset,
          timeUnit: TimeUnit.Day,
        });
      }
    };

    result.push(
      {
        key: 'divider-comparison-end-date',
        itemType: ContextualMenuItemType.Divider,
      },
      {
        key: 'custom',
        onRenderContent: () => (
          <ContextualMenuOption
            label={'Date Range Ending At'}
            hasSubMenus={true}
          />
        ),
        subMenuProps: {
          items: [
            {
              key: 'sub-date-picker',
              onRender: (ev, dismissMenu) => {
                const onChange = (d: Date | null) => {
                  onDatePickerChange(d);
                  dismissMenu(ev, true);
                };
                return (
                  <ComparisonConfigPickerUI.DatePickerWrapper>
                    <ReactDatePicker
                      inline
                      selected={selectedCalendarDate}
                      onChange={onChange}
                      disabledKeyboardNavigation={true}
                      formatWeekDay={(weekdayStr) => weekdayStr.charAt(0)}
                      renderCustomHeader={DateRangeCustomHeader}
                    />
                  </ComparisonConfigPickerUI.DatePickerWrapper>
                );
              },
            },
          ],
        },
      },
    );

    return result;
  }, [queryEnd, selectedCalendarDate, handleSetComparisonConfig]);

  const label = comparisonConfig
    ? `Compare to ${getComparisonRangeDisplay(comparisonConfig, builderDates)}`
    : 'Compare';

  const comparisonConfigIsSelected = !isNil(comparisonConfig);
  const disableComparisonConfig =
    selectedOptions.type === QueryType.Expression &&
    !selectedOptions.includeMissingDates;
  return (
    <BuilderUI.BuilderFieldContainer>
      <KubitContextualMenu
        allowTriggerOnClick={true}
        items={dropdownItems}
        disabled={disableComparisonConfig}
        trigger={
          <div>
            <KubitTooltip
              content={
                disableComparisonConfig ? (
                  <span>
                    Comparison is not available when <b>Excluding</b> missing
                    dates.
                  </span>
                ) : (
                  queryComparisonConfigInfo.content
                )
              }
            >
              <BorderedDropdownLabel
                isAlwaysActive={comparisonConfigIsSelected}
                isDisabled={disableComparisonConfig}
              >
                {label}
              </BorderedDropdownLabel>
            </KubitTooltip>
          </div>
        }
      />
      {comparisonConfigIsSelected && (
        <SquareRemoveButton
          onClick={() => handleSetComparisonConfig(null)}
          content={'Remove Compare'}
        />
      )}
    </BuilderUI.BuilderFieldContainer>
  );
};
